import React, { useEffect, useRef, useState } from 'react';
import { ArticleDetails } from "../../core/models/interfaces/article-details.interface";
import { useArticleApi } from "../../core/hooks/use-article-api";
import { useLoading } from "../../core/hooks/use-loading.hook";
import { State, ViewStates } from "@common/components/view-states";
import { ViewState } from "@common/models/enums/viev-state.enum";
import PublicArticleContent from "../components/public-article-content";
import PublicRecommendedSpecialists from "../components/public-recommended-specialists";
import PublicArticleLoading from "../components/ui/public-article-loading";
import PublicArticleEmpty from "../components/ui/public-article-empty";
import PublicStickyCta from "../components/ui/public-sticky-cta";
import PublicArticleHeader from "../components/ui/public-article-header";
import sanitizeUrlString from "@utils/sanitize-url-string";

interface PublicArticleViewProps {
    articleTitle?: string;
    appId?: string
}

const PublicArticleView: React.FC<PublicArticleViewProps> = ({ articleTitle, appId }) => {
    const { getPublicArticle, error } = useArticleApi();
    const { isLoading } = useLoading();
    const [article, setArticle] = useState<ArticleDetails | null>(null);
    const [isSpecialistsOpen, setIsSpecialistsOpen] = useState<boolean>(false);
    const specialistsRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!articleTitle) return;
        (async () => {
            try {
                const res = await getPublicArticle(sanitizeUrlString(articleTitle), sanitizeUrlString(appId!));
                setArticle(res);
            } catch (e) {
            }
        })();
    }, [articleTitle]);

    const handleImageLoading = (container: HTMLElement): void => {
        const images = container.querySelectorAll('img');
        images.forEach((img: HTMLImageElement) => {
            const parentWrapper = img.closest('.image-node') as HTMLElement;
            if (img.complete && img.naturalHeight !== 0) {
                img.classList.remove('loading');
                if (parentWrapper) parentWrapper.classList.remove('loading');
            } else {
                img.classList.add('loading');
                if (parentWrapper) parentWrapper.classList.add('loading');
                img.onload = () => {
                    img.classList.remove('loading');
                    if (parentWrapper) parentWrapper.classList.remove('loading');
                };
                img.onerror = () => {
                    img.classList.remove('loading');
                    img.classList.add('error');
                    if (parentWrapper) {
                        parentWrapper.classList.remove('loading');
                        parentWrapper.classList.add('error');
                    }
                };
            }
        });
    };

    const openSpecialistsPanel = (): void => {
        setIsSpecialistsOpen(true);
        setTimeout(() => {
            const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024; // lg breakpoint
            if (!isMobile) return;
            const el = specialistsRef.current || document.getElementById('recommended-specialists');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
            }
        }, 0);
    };

    const closeSpecialistsPanel = (): void => {
        setIsSpecialistsOpen(false);
    };

    return (
        <>
            <PublicArticleHeader article={article} />

            <ViewStates loading={isLoading(["getPublicArticle"])} error={error} data={article ? [article] : []}>
                <State state={ViewState.LOADING}>
                    <PublicArticleLoading />
                </State>
                <State state={ViewState.EMPTY}>
                    <PublicArticleEmpty />
                </State>
                <State state={ViewState.FETCHED}>
                    {article && (
                        <div className={isSpecialistsOpen ? "w-fit mx-auto lg:flex lg:gap-8 lg:items-start pb-12 sm:pb-8" : "w-full pb-12 sm:pb-8"}>
                            <div className="min-w-0 mx-auto lg:max-w-[1000px] pt-8">
                                <PublicArticleContent article={article} onMountContent={handleImageLoading} />
                                {article.recommendedSpecialists?.length > 0 && isSpecialistsOpen && (
                                    <div className="lg:hidden">
                                        <div className="border-0 border-t border-[#B3CBCC] border-solid mt-8"></div>
                                        <PublicRecommendedSpecialists
                                            ref={specialistsRef}
                                            specialists={article.recommendedSpecialists}
                                            onClose={closeSpecialistsPanel}
                                        />
                                    </div>
                                )}
                            </div>
                            {article.recommendedSpecialists?.length > 0 && isSpecialistsOpen && (
                                <div className="hidden min-w-fit lg:block w-[380px] min-h-fit sticky top-[74px] z-10">
                                    <PublicRecommendedSpecialists
                                        specialists={article.recommendedSpecialists}
                                        onClose={closeSpecialistsPanel}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </State>
                <State state={ViewState.ERROR}>
                    <PublicArticleEmpty />
                </State>
            </ViewStates>

            {article?.recommendedSpecialists && article.recommendedSpecialists?.length > 0 && (
                <PublicStickyCta onClick={openSpecialistsPanel} onHidden={closeSpecialistsPanel} showText={isSpecialistsOpen}/>
            )}
        </>
    );
};

export default PublicArticleView;


